import { useCallback, useEffect, useState } from 'react';
import { LIST_TABLE_MIN_WIDTH } from '../../constant';
import {
    getListViewMode,
    ListViewEntityKey,
    ListViewMode,
    setListViewMode,
} from './listViewStorage';

type UseListViewModeOptions = {
    defaultMode?: ListViewMode;
};

/**
 * Persisted list view mode. Below LIST_TABLE_MIN_WIDTH (phone) forces tile.
 * Tablet and up can switch tile ↔ table.
 */
export const useListViewMode = (
    entityKey: ListViewEntityKey,
    { defaultMode = 'table' }: UseListViewModeOptions = {},
) => {
    const [width, setWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : LIST_TABLE_MIN_WIDTH,
    );
    const [storedMode, setStoredMode] = useState<ListViewMode>(() =>
        getListViewMode(entityKey, defaultMode),
    );

    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const isNarrow = width < LIST_TABLE_MIN_WIDTH;
    const mode: ListViewMode = isNarrow ? 'tile' : storedMode;

    const setMode = useCallback((next: ListViewMode) => {
        setStoredMode(next);
        setListViewMode(entityKey, next);
    }, [entityKey]);

    return {
        mode,
        setMode,
        isTableForcedOff: isNarrow,
        storedMode,
    };
};
